<script setup lang="ts">
import * as yup from 'yup';
import { useForm } from 'vee-validate';
import type { AdminApiGetArea } from '../../types';

interface FormValues {
  name: string;
  area_type: string;
  parent_id: number;
  site_id: number;
  meters: Array<{
    type: string;
    data_source_id: string[];
  }>;
}

// Props
const modal = defineModel<boolean>();
const props = defineProps<{
  area?: AdminApiGetArea;
  siteId: number;
  parentId: number;
}>();

// Composables
const { t } = useI18n();
const { energyTypesList } = coreEnergyService();
const { resetForm, values, setValues } = useForm<FormValues>({
  validationSchema: yup.object({
    name: yup.string().required(t('global.required_field')),
    area_type: yup.string().required(t('global.required_field')),
    parent_id: yup.number().required(t('global.required_field')),
    site_id: yup.number().required(t('global.required_field')),
    meters: yup.array().of(
      yup.object({
        type: yup.string().required(t('global.required_field')),
        data_source_id: yup.string().required(t('global.required_field')),
      }),
    ),
  }),
  initialValues: {
    name: '',
    area_type: '',
    parent_id: props.parentId,
    site_id: props.siteId,
    meters: [
      {
        type: '',
        data_source_id: [],
      },
    ],
  },
});

// Stores
const { areaTypes } = storeToRefs(areaStore());
const { currentSite } = storeToRefs(adminStore());

// Computed
const areaTypesAsOptions = computed(() => {
  return areaTypes.value.map((areaType) => ({
    key: areaType.value,
    label: t(`area.area_types.${areaType.value}`),
  }));
});

const areasAsOptions = computed(() => {
  const options =
    currentSite.value?.areas.map((area) => ({
      key: area.id.toString(),
      label: area.name,
    })) ?? [];
  return props.siteId ? options : [];
});

// Methods
const addMeter = () => {
  if (values.meters.length >= 1) {
    setValues({
      meters: [...values.meters, { type: '', data_source_id: [] }],
    });
  } else {
    setValues({
      meters: [{ type: '', data_source_id: [] }],
    });
  }
};

const removeMeter = (index: number) => {
  setValues({
    meters: values.meters.filter((_, i) => i !== index),
  });
};

const splitDataSourceId = (dataSourceId: string) => {
  return dataSourceId.split(',').map((id) => id.trim());
};

// Lifecycle
watchEffect(() => {
  if (props.area) {
    setValues({
      name: props.area.name,
      area_type: props.area.area_type,
      parent_id: props.area.parent_id ?? props.parentId,
      site_id: props.siteId,
      meters: props.area.meters.map((meter) => ({
        type: meter.meter_type.name,
        data_source_id: splitDataSourceId(meter.data_source_id),
      })),
    });
  } else {
    resetForm({
      values: {
        parent_id: props.parentId,
        site_id: props.siteId,
        meters: [
          {
            type: '',
            data_source_id: [],
          },
        ],
      },
    });
  }
});
</script>
<template>
  <nrjx-modal v-model="modal" width="800" :title="$t('area.add_area')">
    <div class="grid grid-cols-5 gap-x-4 gap-y-2">
      <!-- Title -->
      <p class="text-sm col-span-2 font-semibold text-gray-600">{{ $t('global.informations') }}</p>
      <p class="text-sm col-span-3 font-semibold text-gray-600">{{ $t('global.meters') }}</p>

      <!-- Content -->
      <div class="col-span-2 pb-6">
        <nrjx-input-select
          name="parent_id"
          :placeholder="$t('area.select_parent')"
          :label="$t('area.parent')"
          searchable
          disabled
          fit-to-select
          item-key="key"
          item-label="label"
          :items="areasAsOptions"
          class="mb-4"
        />

        <ui-form-input-text
          name="name"
          required
          autocomplete="off"
          :placeholder="$t('area.placeholder_area_name')"
          :label="$t('area.name')"
        />

        <nrjx-input-select
          name="area_type"
          required
          item-key="key"
          item-label="label"
          fit-to-select
          searchable
          :placeholder="$t('area.select_area_type')"
          :label="$t('area.area_type')"
          :items="areaTypesAsOptions"
        />
      </div>

      <!-- Meters -->
      <div class="col-span-3 flex flex-col justify-between border-gray-200">
        <div class="space-y-4 max-h-[400px] overflow-y-auto">
          <div v-for="(meter, index) in values.meters" :key="index" class="border border-gray-200 rounded-lg p-2 bg-gray-50">
            <nrjx-input-select
              :label="$t('area.meter_type')"
              :name="`meters[${index}].type`"
              :placeholder="$t('area.placeholder_meter_type')"
              item-key="id"
              item-label="name"
              :items="energyTypesList"
              :hide-details="false"
              fit-to-select
            >
              <template #item="{ item }">
                <div class="flex items-center gap-2">
                  <ui-badge v-if="item.energyType === 'Electricity'" color="primary">{{ item.energyType }}</ui-badge>
                  <!-- <ui-badge v-else-if="item.energyType === 'Gas'" color="orange">{{ item.energyType }}</ui-badge> -->
                  <span class="text-gray-500">{{ item.name }}</span>
                </div>
              </template>
            </nrjx-input-select>
            <ui-form-input-text
              v-for="(dataSourceId, dataSourceIdIndex) in meter.data_source_id"
              :key="dataSourceIdIndex"
              :label="$t('area.data_source_id')"
              :name="`meters[${index}].data_source_id[${dataSourceIdIndex}]`"
              :placeholder="`${dataSourceId}, ...`"
            />
            <ui-button v-if="values.meters.length > 1" left-icon="Trash" class="w-full mt-6" color="delete" @click="removeMeter(index)">
              {{ $t('global.delete') }}
            </ui-button>
          </div>
        </div>

        <ui-button left-icon="Plus" class="w-full mt-4" color="secondary" @click="addMeter">
          {{ $t('area.add_meter') }}
        </ui-button>
      </div>
    </div>

    <template #footer>
      <ui-button color="primary">{{ $t('global.save') }}</ui-button>
    </template>
  </nrjx-modal>
</template>
